import { useLocation } from "react-router-dom";
import { useAppSelector } from "../../../store/hooks";
import {
  EventAdditionalItemsComponent as EventAdditionalItemsComponent_,
  EventAdditionalItemsGroupConfig,
} from "../interfaces/base";
import styled from "@emotion/styled";
import RenderChildren from "../RenderChildren";
import usePageLanguage from "../hooks/usePageLanguage";
import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
  useLayoutEffect,
} from "react";
import { useShoppingCart } from "../context/ShoppingCartContext";
import { Spinner as BootstrapSpinner } from "react-bootstrap";
import "./EventAdditionalItemsComponent.css";
import { getCatalogProductDisplayFields } from "../utilities/catalogProductAdapter";
import { resolveProductImageUrl } from "../utilities/helpers";
import {
  getComponentFromStructureMap,
  matchesEditorTarget,
} from "../utilities/componentIdentity";
import {
  eventCartMutationTarget,
  getEventCartInputKey,
  getEventManualCartLineQuantity,
  resolveEventProductUuid,
} from "../utilities/eventCart";
import {
  clampQuantityToMinAndCap,
  hasMinOrderQtyConstraint,
  isEffectivelySoldOut,
  parseMaxOrderQty,
  resolveDisplayAvailable,
  resolveEffectiveMinQty,
  resolveMinOrderQty,
  resolvePickerDisplayAvailable,
  resolveStartQuantity,
} from "../utilities/quantityCaps";

// ── API helpers ──────────────────────────────────────────────────────

declare var defaultApiConfig: any;
declare var API: any;

function fetchApi<TResponse>(
  url: string,
  config: RequestInit = {},
  jsonType: boolean = true
): Promise<TResponse> {
  const newConfig = {
    ...(typeof defaultApiConfig !== "undefined" ? defaultApiConfig : {}),
    ...config,
    headers: {
      ...(typeof defaultApiConfig !== "undefined" && defaultApiConfig.headers
        ? defaultApiConfig.headers
        : {}),
      ...(config.headers || {}),
    },
  };
  const apiUrl = typeof API !== "undefined" && API.url ? API.url : "";
  if (!jsonType) {
    return fetch(`${apiUrl}${url}`, newConfig).then(
      (data) => data as unknown as TResponse
    );
  }
  return fetch(`${apiUrl}${url}`, newConfig)
    .then((response) => response.json())
    .then((data) => data as TResponse);
}

function getHeaders(params: {} = {}) {
  return {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      ...(typeof defaultApiConfig !== "undefined" &&
      defaultApiConfig.headers &&
      defaultApiConfig.headers.Authorization
        ? { Authorization: defaultApiConfig.headers.Authorization }
        : {}),
    },
    params: params,
  };
}

function fetchApiGet<TResponse>(
  url: string,
  params: {} = {}
): Promise<TResponse> {
  return fetchApi(url, getHeaders(params));
}

// ── Cache ────────────────────────────────────────────────────────────

interface CacheEntry {
  data: any;
  timestamp: number;
  expiresIn: number;
}
const axiosCache = new Map<string, CacheEntry>();
const CACHE_DURATION = 5 * 60 * 1000;

async function cachedAxiosGet(url: string): Promise<any> {
  const cached = axiosCache.get(url);
  const now = Date.now();
  if (cached && now < cached.timestamp + cached.expiresIn) {
    return cached.data;
  }
  // @ts-ignore
  const response = await window.axios.get(url);
  axiosCache.set(url, {
    data: response,
    timestamp: now,
    expiresIn: CACHE_DURATION,
  });
  return response;
}

// ── Styled wrapper ───────────────────────────────────────────────────

const Div = styled("div")(
  null,
  (props) =>
    function (props: any) {
      return { ...props["data-style"] };
    }
);

// ── Helpers ──────────────────────────────────────────────────────────

function formatPrice(price: number | string): string {
  return parseFloat(String(price)).toFixed(2);
}

function formatEventDate(dateString: string): string {
  const date = new Date(dateString);
  const weekday = date.toLocaleString("et-EE", { weekday: "narrow" });
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${weekday} ${day}.${month}.${year} ${hours}:${minutes}`;
}

function normalizeButtonLink(link: string): string {
  const trimmed = link.trim();
  if (!trimmed) return "";
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  if (trimmed.startsWith("/")) return trimmed;
  return `/${trimmed}`;
}

function formatDateForApi(date: Date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/**
 * Parse an Estonian-local datetime string (e.g. "2026-06-21T10:00")
 * and return a UTC ISO string.
 */
function estonianToUtcIso(estonianStr: string): string | null {
  if (!estonianStr) return null;
  try {
    const [datePart, timePart] = estonianStr.split("T");
    if (!datePart || !timePart) return null;
    const [year, month, day] = datePart.split("-").map(Number);
    const [hours, minutes] = timePart.split(":").map(Number);
    if ([year, month, day, hours, minutes].some((v) => isNaN(v))) return null;

    const tentativeUtc = new Date(
      Date.UTC(year, month - 1, day, hours, minutes, 0)
    );
    const formatter = new Intl.DateTimeFormat("en-US", {
      timeZone: "Europe/Tallinn",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    });
    const parts: Record<string, string> = {};
    formatter.formatToParts(tentativeUtc).forEach((p) => {
      parts[p.type] = p.value;
    });
    const tallinnAtThatUtc = new Date(
      Date.UTC(
        Number(parts.year),
        Number(parts.month) - 1,
        Number(parts.day),
        Number(parts.hour),
        Number(parts.minute),
        Number(parts.second || 0)
      )
    );
    const offsetMs = tallinnAtThatUtc.getTime() - tentativeUtc.getTime();
    const utcDate = new Date(tentativeUtc.getTime() - offsetMs);
    return utcDate.toISOString();
  } catch {
    return null;
  }
}

/** Extract date part from an Estonian-local datetime string as a local Date. */
function estonianDatePart(estonianStr: string): Date | null {
  if (!estonianStr) return null;
  try {
    const [datePart] = estonianStr.split("T");
    const [year, month, day] = datePart.split("-").map(Number);
    if ([year, month, day].some((v) => isNaN(v))) return null;
    return new Date(year, month - 1, day);
  } catch {
    return null;
  }
}

// ── Placeholder image ────────────────────────────────────────────────

const PLACEHOLDER_IMAGE =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160' fill='%23ccc'%3E%3Crect width='160' height='160' fill='%23f0f0f0'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-family='sans-serif' font-size='12' fill='%23aaa'%3ENo image%3C/text%3E%3C/svg%3E";

// ── Spinner ──────────────────────────────────────────────────────────

const SimpleSpinner = (): JSX.Element => (
  <BootstrapSpinner animation="border" role="status" as="div" />
);

// ── Translations ─────────────────────────────────────────────────────

const translations: Record<string, any> = {
  et: {
    loading: "Laen lisatooteid...",
    no_products: "Lisatooted puuduvad",
    error: "Lisatoodete laadimine ebaõnnestus",
    no_events_found: "Üritusi ei leitud valitud kuupäevadega",
    sold_out: "Välja müüdud!",
    available: "Saadaval",
  },
  en: {
    loading: "Loading additional items...",
    no_products: "No additional items available",
    error: "Failed to load additional items",
    no_events_found: "No events found for the selected dates",
    sold_out: "Sold out!",
    available: "Available",
  },
};

// ── Types ────────────────────────────────────────────────────────────

interface AdditionalProduct {
  uuid: string;
  productUuid: string;
  name: string;
  maxQty: number;
  maxOrderQty: number;
  minQty: number;
  minOrderQty: number;
  soldQty: number;
  maxAvailableQty: number;
  price: string;
  salesPeriodStartBeforeEventDatetime: string | null;
  salesPeriodEndBeforeEventDatetime: string | null;
}

interface AdditionalSection {
  sectionUuid: string;
  name: string;
  products: AdditionalProduct[];
}

function getAdditionalProductmaxOrderQty(
  product: AdditionalProduct
): number | undefined {
  return parseMaxOrderQty(product.maxOrderQty);
}

function getAdditionalEffectiveMin(
  product: AdditionalProduct,
  enforceFirstOrderMin: boolean
): number {
  return resolveEffectiveMinQty(
    product.minQty,
    product.minOrderQty,
    enforceFirstOrderMin
  );
}

function getAdditionalProductEffectiveMax(product: AdditionalProduct): number {
  return resolveDisplayAvailable(product.maxAvailableQty, product.maxOrderQty);
}

/** Minimal event shape used by this component (from API) */
interface EventWithAdditionalProducts {
  hash: string;
  start: string;
  hasActiveSellOrders?: boolean;
  additionalProducts?: AdditionalSection[];
}

interface MatchedEvent {
  event: EventWithAdditionalProducts;
  urlParam: string;
  groupName: string;
}

interface SectionHeaderProps {
  sectionIndex: number;
  sectionName: string;
  eventStart: string;
  groupName: string;
}

interface ProductCardProps {
  product: AdditionalProduct;
  event: EventWithAdditionalProducts;
  currentQty: number;
  effectiveMax: number;
  availableQty: number;
  displayValue: string;
  imageUrl: string;
  displayName: string;
  description: string;
  unit: string;
  isSoldOut: boolean;
  hideZeroPrice: boolean;
  availableLabel: string;
  soldOutLabel: string;
  onIncrease: () => void;
  onDecrease: () => void;
  onQtyInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onQtyInputBlur: () => void;
  showReadMore: boolean;
  readMoreLabel: string;
}

interface DescriptionProps {
  html: string;
  showReadMore: boolean;
  readMoreLabel: string;
}

function EventAdditionalItemDescription({
  html,
  showReadMore,
  readMoreLabel,
}: DescriptionProps): JSX.Element | null {
  const [expanded, setExpanded] = useState(false);
  const [isClamped, setIsClamped] = useState(showReadMore);
  const [showMoreBtn, setShowMoreBtn] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  const checkOverflow = useCallback(() => {
    const el = contentRef.current;
    if (!el || !showReadMore || expanded) {
      setShowMoreBtn(false);
      return;
    }
    const overflow = el.scrollHeight > el.clientHeight + 1;
    setShowMoreBtn(overflow);
    if (!overflow) {
      setIsClamped(false);
    }
  }, [showReadMore, expanded]);

  useLayoutEffect(() => {
    if (!showReadMore || expanded) {
      setIsClamped(false);
      setShowMoreBtn(false);
      return;
    }
    setIsClamped(true);
  }, [html, showReadMore, expanded]);

  useLayoutEffect(() => {
    checkOverflow();
  }, [checkOverflow, isClamped, html]);

  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    const observer = new ResizeObserver(() => checkOverflow());
    observer.observe(el);
    return () => observer.disconnect();
  }, [checkOverflow]);

  if (!html) return null;

  if (!showReadMore || expanded) {
    return (
      <div
        className="event-additional-items-product-description"
        dangerouslySetInnerHTML={{ __html: html }}
      />
    );
  }

  return (
    <div className="event-additional-items-product-description-wrap">
      <div
        ref={contentRef}
        className={
          "event-additional-items-product-description" +
          (isClamped
            ? " event-additional-items-product-description--clamped"
            : "")
        }
        dangerouslySetInnerHTML={{ __html: html }}
      />
      {showMoreBtn && readMoreLabel && (
        <span className="event-additional-items-read-more-suffix">
          <button
            type="button"
            className="event-additional-items-read-more"
            onClick={() => setExpanded(true)}
          >
            {readMoreLabel}
          </button>
        </span>
      )}
    </div>
  );
}

// ── Extracted subcomponents ────────────────────────────────────────

function EventAdditionalItemsSectionHeader({
  sectionIndex,
  sectionName,
  eventStart,
  groupName,
}: SectionHeaderProps): JSX.Element {
  return (
    <div className="event-additional-items-section-header">
      <div className="event-additional-items-flex-row">
        <span className="event-additional-items-section-number">
          {sectionIndex}
        </span>
        <span className="event-additional-items-section-name">
          {sectionName}
        </span>
      </div>
      <div className="event-additional-items-section-meta">
        <span>{formatEventDate(eventStart)}</span>
        <span>| {groupName}</span>
      </div>
    </div>
  );
}

function EventAdditionalItemProductCard({
  product,
  event,
  currentQty,
  effectiveMax,
  availableQty,
  displayValue,
  imageUrl,
  displayName,
  description,
  unit,
  isSoldOut,
  hideZeroPrice,
  availableLabel,
  soldOutLabel,
  onIncrease,
  onDecrease,
  onQtyInputChange,
  onQtyInputBlur,
  showReadMore,
  readMoreLabel,
}: ProductCardProps): JSX.Element {
  return (
    <div className="event-additional-items-product">
      <img
        className="event-additional-items-product-image"
        src={imageUrl}
        alt={displayName}
        onError={(e) => {
          (e.target as HTMLImageElement).src = PLACEHOLDER_IMAGE;
        }}
      />
      <div className="event-additional-items-product-body">
        <div className="event-additional-items-product-name">
          {displayName}
        </div>
        <div className="event-additional-items-controls-row">
          {!isSoldOut && (
            <>
              <div className="event-additional-items-flex-column">
                <div className="event-additional-items-flex-row">
                  <div
                    className={`event-additional-items-qty-stepper${
                      currentQty > 0
                        ? " event-additional-items-qty-stepper--active"
                        : ""
                    }`}
                  >
                    <button
                      className="event-additional-items-qty-btn"
                      disabled={currentQty <= 0}
                      onClick={onDecrease}
                    >
                      −
                    </button>
                    <input
                      className="event-additional-items-qty-input"
                      type="text"
                      inputMode="numeric"
                      min={0}
                      max={effectiveMax === Infinity ? undefined : effectiveMax}
                      value={displayValue}
                      onChange={onQtyInputChange}
                      onBlur={onQtyInputBlur}
                    />
                    <button
                      className="event-additional-items-qty-btn"
                      disabled={currentQty >= effectiveMax}
                      onClick={onIncrease}
                    >
                      +
                    </button>
                  </div>
                  {!(hideZeroPrice && parseFloat(product.price) === 0) && (
                    <span className="event-additional-items-product-price">
                      {formatPrice(product.price)}€ / {unit}
                    </span>
                  )}
                </div>
                <div className="event-additional-items-available-qty">
                  {availableLabel}: {availableQty}
                </div>
              </div>
            </>
          )}
          {isSoldOut && (
            <span className="event-additional-items-sold-out">
              {soldOutLabel}
            </span>
          )}
        </div>
        <EventAdditionalItemDescription
          html={description}
          showReadMore={showReadMore}
          readMoreLabel={readMoreLabel}
        />
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════
// Component
// ═════════════════════════════════════════════════════════════════════

export default function EventAdditionalItemsComponent({
  id,
  uuid,
  attributes,
  childElements,
  childWebpageComponentRelations,
}: EventAdditionalItemsComponent_) {
  const editorId = useAppSelector((state) => state.textEditor?.get("id"));
  const component = (useAppSelector((state) =>
    getComponentFromStructureMap(state.builder.structure.components, uuid ?? id)
  ) as EventAdditionalItemsComponent_) ?? ({
    id: typeof id === "number" ? id : -1,
    parent_id: null,
    childElements: childElements ?? [],
    childWebpageComponentRelations: childWebpageComponentRelations ?? [],
    sort_order: 0,
    type: "eventAdditionalItemsComponent",
    uuid: uuid ?? null,
    version: 1,
    webpage_id: 0,
    attributes,
  } as EventAdditionalItemsComponent_);

  const isBuilder = false;

  const {
    increaseCartQuantity,
    decreaseCartQuantity,
    removeFromCart,
    setCartQuantity,
    cartItems,
    getCatalogProductByUuid,
    isFirstOrderMinQtyEnforced,
    registerEventSellOrderFlags,
    order,
  } = useShoppingCart();

  const location = useLocation();
  const locale = usePageLanguage();
  const t = translations[locale] || translations.et;

  // ── Parse URL search string ──────────────────────────────────────

  const locationSearch = location.search;

  // ── Core state ──────────────────────────────────────────────────

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [missingUrlParams, setMissingUrlParams] = useState(false);
  const [matchedEvents, setMatchedEvents] = useState<MatchedEvent[]>([]);

  // Stable key for the configured groups so the effect can depend on it
  const eventGroups = useMemo(
    () =>
      ((component.attributes as any)?.event_groups as
        | EventAdditionalItemsGroupConfig[]
        | undefined) ?? [],
    [JSON.stringify((component.attributes as any)?.event_groups)]
  );

  const attrs = component.attributes;
  const hideZeroPrice = attrs.hide_zero_price ?? false;
  const showReadMore = attrs.show_read_more ?? false;
  const showReadMoreText = attrs.show_read_more_text ?? "";
  const noUrlParamShowText = attrs.no_url_param_show_text ?? false;
  const noUrlParamText = attrs.no_url_param_text ?? "";
  const noUrlParamShowButton = attrs.no_url_param_show_button ?? false;
  const noUrlParamButtonText = attrs.no_url_param_button_text ?? "";
  const noUrlParamButtonLink = attrs.no_url_param_button_link ?? "";

  const hasAnyConfiguredUrlParam = useMemo(() => {
    const uniqueParams = [
      ...new Set(eventGroups.map((g) => g.urlParam).filter(Boolean)),
    ];
    if (uniqueParams.length === 0) return false;
    const sp = new URLSearchParams(locationSearch);
    return uniqueParams.some((p) => sp.get(p));
  }, [eventGroups, locationSearch]);

  // ── Cart helpers ────────────────────────────────────────────────

  const getEventRef = (event: {
    hash: string | undefined;
  }): string =>
    event.hash ?? "";

  const getCartQty = useCallback(
    (product: AdditionalProduct, eventRef: string | undefined): number => {
      const productUuid = resolveEventProductUuid(product);
      return getEventManualCartLineQuantity(
        cartItems,
        eventCartMutationTarget({
          productUuid: productUuid ?? "",
          eventRef: eventRef,
        })
      );
    },
    [cartItems]
  );

  // ── Local input value tracking ─────────────────────────────────

  const [inputValues, setInputValues] = useState<Record<string, string>>({});
  const inputValuesRef = useRef(inputValues);
  inputValuesRef.current = inputValues;

  const getInputKey = (product: AdditionalProduct, eventRef: string) =>
    getEventCartInputKey(
      resolveEventProductUuid(product) ?? "",
      eventRef
    );

  // ── Cart actions ───────────────────────────────────────────────

  const getEffectiveMax = useCallback(
    (product: AdditionalProduct): number =>
      getAdditionalProductEffectiveMax(product),
    []
  );

  const handleIncrease = useCallback(
    (product: AdditionalProduct, event: EventWithAdditionalProducts) => {
      const eventRef = getEventRef(event);
      const enforceFirstOrderMin = isFirstOrderMinQtyEnforced(eventRef);
      const effectiveMin = getAdditionalEffectiveMin(product, enforceFirstOrderMin);
      const minForUi = effectiveMin > 1 ? effectiveMin : undefined;
      const effectiveMax = getEffectiveMax(product);
      if (isEffectivelySoldOut(minForUi, effectiveMax)) return;
      const currentQty = getCartQty(product, eventRef);
      const productUuid = resolveEventProductUuid(product);
      if (currentQty >= effectiveMax) return;
      const start = new Date(event.start).toISOString();
      const maxOrderQty = getAdditionalProductmaxOrderQty(product);
      if (currentQty === 0 && hasMinOrderQtyConstraint(minForUi)) {
        const startQty = resolveStartQuantity(minForUi, effectiveMax);
        increaseCartQuantity(
          productUuid ?? "",
          eventRef,
          start,
          true,
          undefined,
          product.maxAvailableQty,
          maxOrderQty,
          product.minOrderQty,
          product.minQty,
          event.hasActiveSellOrders,
          "eventAdditional",
        );
        if (startQty > 1)
          setCartQuantity(startQty, productUuid ?? "", eventRef, undefined);
      } else {
        increaseCartQuantity(
          productUuid ?? "",
          eventRef,
          start,
          true,
          undefined,
          product.maxAvailableQty,
          maxOrderQty,
          product.minOrderQty,
          product.minQty,
          event.hasActiveSellOrders,
          "eventAdditional",
        );
      }
    },
    [
      increaseCartQuantity,
      getEffectiveMax,
      getCartQty,
      getEventRef,
      setCartQuantity,
      isFirstOrderMinQtyEnforced,
    ]
  );

  const handleDecrease = useCallback(
    (product: AdditionalProduct, event: EventWithAdditionalProducts) => {
      const eventRef = getEventRef(event);
      const currentQty = getCartQty(product, eventRef);
      const productUuid = resolveEventProductUuid(product);
      const minQty = getAdditionalEffectiveMin(
        product,
        isFirstOrderMinQtyEnforced(eventRef)
      );
      if (currentQty <= minQty) {
        removeFromCart(productUuid ?? "", eventRef);
      } else {
        decreaseCartQuantity(productUuid ?? "", eventRef);
      }
    },
    [getCartQty, getEventRef, decreaseCartQuantity, removeFromCart, isFirstOrderMinQtyEnforced]
  );

  const handleQtyInputChange = useCallback(
    (
      e: React.ChangeEvent<HTMLInputElement>,
      product: AdditionalProduct,
      event: EventWithAdditionalProducts
    ) => {
      const eventRef = getEventRef(event);
      const enforceFirstOrderMin = isFirstOrderMinQtyEnforced(eventRef);
      const effectiveMin = getAdditionalEffectiveMin(product, enforceFirstOrderMin);
      const minForUi = effectiveMin > 1 ? effectiveMin : undefined;
      const key = getInputKey(product, eventRef);
      const productUuid = resolveEventProductUuid(product);
      const rawValue = e.target.value;

      if (rawValue === "") {
        setInputValues((prev) => ({ ...prev, [key]: "" }));
        return;
      }
      if (!/^\d+$/.test(rawValue)) return;

      let newQty = parseInt(rawValue, 10);
      if (isNaN(newQty)) return;

      const effectiveMax = getEffectiveMax(product);
      if (newQty < 0) newQty = 0;
      newQty = clampQuantityToMinAndCap(newQty, minForUi, effectiveMax);

      setInputValues((prev) => ({ ...prev, [key]: String(newQty) }));

      const currentQty = getCartQty(product, eventRef);
      if (newQty === currentQty) return;

      if (newQty === 0) {
        removeFromCart(productUuid ?? "", eventRef);
      } else if (currentQty === 0 && newQty > 0) {
        const start = new Date(event.start).toISOString();
        increaseCartQuantity(
          productUuid ?? "",
          eventRef,
          start,
          true,
          undefined,
          product.maxAvailableQty,
          getAdditionalProductmaxOrderQty(product),
          product.minOrderQty,
          product.minQty,
          event.hasActiveSellOrders,
          "eventAdditional",
        );
        if (newQty > 1) {
          setCartQuantity(
            newQty,
            productUuid ?? "",
            eventRef,
            undefined,
          );
        }
      } else {
        setCartQuantity(
          newQty,
          productUuid ?? "",
          eventRef,
          undefined,
        );
      }
    },
    [
      getCartQty,
      getEventRef,
      getEffectiveMax,
      removeFromCart,
      increaseCartQuantity,
      setCartQuantity,
    ]
  );

  const handleQtyInputBlur = useCallback(
    (product: AdditionalProduct, event: EventWithAdditionalProducts) => {
      const eventRef = getEventRef(event);
      const key = getInputKey(product, eventRef);
      const productUuid = resolveEventProductUuid(product);
      const localVal = inputValuesRef.current[key];
      setInputValues((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      if (localVal === "") {
        const currentQty = getCartQty(product, eventRef);
        if (currentQty > 0) removeFromCart(productUuid ?? "", eventRef);
      }
    },
    [getCartQty, getEventRef, removeFromCart]
  );

  // ── Fetch events for a group ───────────────────────────────────

  const fetchEventsForGroup = useCallback(
    async (
      date: Date,
      groupHash: string
    ): Promise<EventWithAdditionalProducts[]> => {
      const dateStr = formatDateForApi(date);
      let events: EventWithAdditionalProducts[] = [];

      if (isBuilder) {
        const resp = await fetchApiGet<any>(
          `/events?date=${encodeURIComponent(
            dateStr
          )}&groupHash=${groupHash}&perPage=100&sortBy=start&direction=asc`
        );
        if (Array.isArray(resp)) events = resp;
        else if (resp?.data && Array.isArray(resp.data)) events = resp.data;
      } else {
        const resp = await cachedAxiosGet(
          `events?date=${encodeURIComponent(
            dateStr
          )}&groupHash=${groupHash}&perPage=100&sortBy=start&direction=asc`
        );
        if (resp.data?.data) events = resp.data.data;
        else if (Array.isArray(resp.data)) events = resp.data;
      }
      return events;
    },
    [isBuilder]
  );

  const findEventByStartTime = useCallback(
    (
      events: EventWithAdditionalProducts[],
      targetUtcIso: string
    ): EventWithAdditionalProducts | null => {
      if (!targetUtcIso || events.length === 0) return null;
      const targetMs = new Date(targetUtcIso).getTime();
      return (
        events.find((e) => new Date(e.start).getTime() === targetMs) || null
      );
    },
    []
  );

  // ── Load events on mount / URL change ─────────────────────────
  //
  // For each unique URL param across the configured groups, read the
  // corresponding value from the URL. If that value is present, query
  // every group that is mapped to that param and try to find a matching
  // event by its start datetime.

  useEffect(() => {
    if (eventGroups.length === 0) {
      setMissingUrlParams(false);
      setError("No event groups configured");
      setLoading(false);
      return;
    }

    const sp = new URLSearchParams(locationSearch);

    // Collect the unique param names used by the configured groups
    const uniqueParams = [
      ...new Set(eventGroups.map((g) => g.urlParam).filter(Boolean)),
    ];

    const hasAnyParam = uniqueParams.some((p) => sp.get(p));
    if (!hasAnyParam) {
      const showNoUrlButton =
        noUrlParamShowButton &&
        Boolean(noUrlParamButtonText.trim()) &&
        Boolean(normalizeButtonLink(noUrlParamButtonLink));
      const showNoUrlCustomEmpty = noUrlParamShowText || showNoUrlButton;

      setMissingUrlParams(showNoUrlCustomEmpty);
      setError(null);
      setMatchedEvents([]);
      setLoading(false);
      return;
    }

    const loadEvents = async () => {
      setLoading(true);
      setError(null);
      setMissingUrlParams(false);
      const allMatched: MatchedEvent[] = [];

      try {
        for (const paramName of uniqueParams) {
          const paramValue = sp.get(paramName) || "";
          if (!paramValue) continue;

          const utcIso = estonianToUtcIso(paramValue);
          const datePart = estonianDatePart(paramValue);
          if (!utcIso || !datePart) continue;

          // All groups that listen to this URL param
          const groupsForParam = eventGroups.filter(
            (g) => g.urlParam === paramName
          );

          for (const groupConfig of groupsForParam) {
            const events = await fetchEventsForGroup(
              datePart,
              groupConfig.groupHash
            );
            const match = findEventByStartTime(events, utcIso);
            if (match && (match.additionalProducts?.length ?? 0) > 0) {
              allMatched.push({
                event: match,
                urlParam: paramName,
                groupName: groupConfig.groupName,
              });
            }
          }
        }

        setMatchedEvents(allMatched);
        registerEventSellOrderFlags(
          allMatched.map((match) => match.event)
        );
        if (allMatched.length === 0) setError(t.no_events_found);
      } catch (err) {
        console.error("Error loading additional items events:", err);
        setError(t.error);
      } finally {
        setLoading(false);
      }
    };

    loadEvents();
  }, [
    eventGroups,
    locationSearch,
    t,
    noUrlParamShowText,
    noUrlParamShowButton,
    noUrlParamButtonText,
    noUrlParamButtonLink,
  ]);

  // ── Build flattened section list across matched events ─────────

  const allSections = useMemo(() => {
    const sections: Array<{
      sectionIndex: number;
      sectionUuid: string;
      sectionName: string;
      products: AdditionalProduct[];
      event: EventWithAdditionalProducts;
      groupName: string;
    }> = [];
    let index = 1;

    for (const matched of matchedEvents) {
      const additionalProducts: AdditionalSection[] =
        matched.event.additionalProducts || [];
      for (const section of additionalProducts) {
        sections.push({
          sectionIndex: index++,
          sectionUuid: section.sectionUuid,
          sectionName: section.name,
          products: section.products,
          event: matched.event,
          groupName: matched.groupName,
        });
      }
    }
    return sections;
  }, [matchedEvents]);

  // Clamp cart quantities when product caps (incl. maxOrderQty) are known.
  useEffect(() => {
    if (allSections.length === 0) return;

    for (const section of allSections) {
      const eventRef = getEventRef(section.event);
      for (const product of section.products) {
        const productUuid = resolveEventProductUuid(product);
        if (!productUuid) continue;

        const currentQty = getEventManualCartLineQuantity(
          cartItems,
          eventCartMutationTarget({ productUuid, eventRef })
        );
        if (currentQty <= 0) continue;

        const effectiveMax = getAdditionalProductEffectiveMax(product);
        if (currentQty <= effectiveMax) continue;

        if (effectiveMax <= 0) {
          removeFromCart(productUuid, eventRef);
        } else {
          setCartQuantity(effectiveMax, productUuid, eventRef);
        }
      }
    }
  }, [allSections, cartItems, getEventRef, removeFromCart, setCartQuantity]);

  // ── Resolve product data from products.json ─────────────────────

  const resolveCatalogProduct = useCallback(
    (product: AdditionalProduct) => {
      const productUuid = resolveEventProductUuid(product);
      if (!productUuid) {
        return undefined;
      }
      return getCatalogProductByUuid(productUuid);
    },
    [getCatalogProductByUuid]
  );

  const getProductImage = useCallback(
    (product: AdditionalProduct): string => {
      const catalogProduct = resolveCatalogProduct(product);
      const display = catalogProduct
        ? getCatalogProductDisplayFields(catalogProduct, locale)
        : null;
      return resolveProductImageUrl(display?.firstFileUrl);
    },
    [resolveCatalogProduct, locale]
  );

  const getProductDisplayName = useCallback(
    (product: AdditionalProduct): string => {
      const catalogProduct = resolveCatalogProduct(product);
      const display = catalogProduct
        ? getCatalogProductDisplayFields(catalogProduct, locale)
        : null;
      if (display?.displayName) return display.displayName;
      return product.name;
    },
    [resolveCatalogProduct, locale]
  );
  const getProductDescription = useCallback(
    (product: AdditionalProduct): string => {
      const catalogProduct = resolveCatalogProduct(product);
      const display = catalogProduct
        ? getCatalogProductDisplayFields(catalogProduct, locale)
        : null;
      if (display?.description) return display.description;
      return "";
    },
    [resolveCatalogProduct, locale]
  );

  const getUnitShortname = useCallback(
    (product: AdditionalProduct): string => {
      const productUuid = resolveEventProductUuid(product);
      if (!productUuid) {
        return "in";
      }
      return getCatalogProductByUuid(productUuid)?.unitShortname || "in";
    },
    [getCatalogProductByUuid]
  );

  // ── Wrapper class helper ───────────────────────────────────────

  const baseClasses = attributes?.classes ?? "";
  const wrapperClass =
    baseClasses +
    (baseClasses !== "container" && baseClasses !== "container-fluid"
      ? " builder-component builder-component__event_additional_items_component"
      : "") +
    (matchesEditorTarget({ id, uuid }, editorId) ? " builder-component--active" : "");

  // ── Loading state ──────────────────────────────────────────────

  if (loading) {
    return (
      <Div
        id={id.toString()}
        key={id}
        data-style={attributes?.styles || {}}
        className={wrapperClass}
        data-component-type="section"
      >
        <div className="event-additional-items-loading">
          <SimpleSpinner />
          <p className="event-additional-items-loading-text">{t.loading}</p>
        </div>
      </Div>
    );
  }

  // ── Error / empty state ────────────────────────────────────────

  if ((error || missingUrlParams) && allSections.length === 0) {
    const buttonHref = normalizeButtonLink(noUrlParamButtonLink);
    const showNoParamButton =
      missingUrlParams &&
      noUrlParamShowButton &&
      Boolean(noUrlParamButtonText.trim()) &&
      Boolean(buttonHref);

    return (
      <Div
        id={id.toString()}
        key={id}
        data-style={attributes?.styles || {}}
        className={wrapperClass}
        data-component-type="section"
      >
        <RenderChildren
          key={id + "children"}
          childWebpageComponentRelations={childWebpageComponentRelations}
        />
        {missingUrlParams ? (
          <div className="event-additional-items-empty-message">
            {noUrlParamShowText && noUrlParamText.trim() && (
              <p className="event-additional-items-empty-message-text">
                {noUrlParamText}
              </p>
            )}
            {showNoParamButton && (
              <a href={buttonHref} className="event-additional-items-empty-button">
                {noUrlParamButtonText}
              </a>
            )}
          </div>
        ) : (
          <p className="event-additional-items-error">{error}</p>
        )}
      </Div>
    );
  }

  // ── Main render ────────────────────────────────────────────────

  return (
    <Div
      id={id.toString()}
      key={id}
      data-style={attributes?.styles || {}}
        className={`${wrapperClass} event-additional-items-wrapper`}
        data-component-type="section"
    >
      <RenderChildren
        key={id + "children"}
        childWebpageComponentRelations={childWebpageComponentRelations}
      />

      <div className="event-additional-items">
        {allSections.map((section) => (
          <div
            key={section.sectionUuid}
            className="event-additional-items-section"
          >
            <EventAdditionalItemsSectionHeader
              sectionIndex={section.sectionIndex}
              sectionName={section.sectionName}
              eventStart={section.event.start}
              groupName={section.groupName}
            />

            {section.products.map((product) => {
              const eventRef = getEventRef(section.event);
              const enforceFirstOrderMin = isFirstOrderMinQtyEnforced(eventRef);
              const effectiveMin = getAdditionalEffectiveMin(
                product,
                enforceFirstOrderMin
              );
              const minForUi = effectiveMin > 1 ? effectiveMin : undefined;
              const currentQty = getCartQty(product, eventRef);
              const effectiveMax = getEffectiveMax(product);
              const isSoldOut = isEffectivelySoldOut(
                minForUi,
                effectiveMax
              );
              const availableQty = resolvePickerDisplayAvailable(
                minForUi,
                effectiveMax
              );
              const inputKey = getInputKey(product, eventRef);
              const localInputVal = inputValues[inputKey];
              const displayValue =
                localInputVal !== undefined
                  ? localInputVal
                  : String(currentQty);
              return (
                <EventAdditionalItemProductCard
                  key={`${product.uuid}-${eventRef}`}
                  product={product}
                  event={section.event}
                  currentQty={currentQty}
                  effectiveMax={effectiveMax}
                  availableQty={availableQty}
                  displayValue={displayValue}
                  imageUrl={getProductImage(product)}
                  displayName={getProductDisplayName(product)}
                  description={getProductDescription(product)}
                  unit={getUnitShortname(product)}
                  isSoldOut={isSoldOut}
                  hideZeroPrice={hideZeroPrice}
                  availableLabel={t.available}
                  soldOutLabel={t.sold_out}
                  onIncrease={() => handleIncrease(product, section.event)}
                  onDecrease={() => handleDecrease(product, section.event)}
                  onQtyInputChange={(e) =>
                    handleQtyInputChange(e, product, section.event)
                  }
                  onQtyInputBlur={() =>
                    handleQtyInputBlur(product, section.event)
                  }
                  showReadMore={showReadMore}
                  readMoreLabel={showReadMoreText}
                />
              );
            })}
          </div>
        ))}

        {allSections.length === 0 && hasAnyConfiguredUrlParam && (
          <p className="event-additional-items-error">{t.no_products}</p>
        )}
      </div>
    </Div>
  );
}
